import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faJsSquare, faPhp, faPython } from '@fortawesome/free-brands-svg-icons';

export type LanguageKey = 'javascript' | 'php' | 'python';

export type LanguageSample = {
  name: string;
  icon: IconDefinition;
  createOrder: string;
  checkStatus: string;
};

/** ตัวอย่างโค้ดของแท็บ Code Examples — ฝัง API key / base url ของ merchant ที่ล็อกอินอยู่ */
export const buildLanguageSamples = (apiKey: string, baseUrl: string): Record<LanguageKey, LanguageSample> => ({
  javascript: {
    name: 'JavaScript',
    icon: faJsSquare,
    createOrder: `const axios = require('axios');

const API_KEY = '${apiKey}';
const API_SECRET = 'sk_your_api_secret';

async function createOrder() {
  try {
    const response = await axios.post(
      '${baseUrl}/orders',
      {
        amount: 100.00,
        customer_name: 'นาย A',
        customer_email: 'customer@example.com',
        customer_phone: '0812345678',
        reference_id: 'YOUR-ORDER-123',
        bank_name: 'SCB',
        bank_number: '1234567890'
      },
      {
        headers: {
          'X-API-Key': API_KEY,
          'X-API-Secret': API_SECRET,
          'Content-Type': 'application/json'
        }
      }
    );

    console.log('Order Created:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error:', error.response?.data);
  }
}`,
    checkStatus: `async function checkOrderStatus(orderNumber) {
  try {
    const response = await axios.get(
      \`\${baseUrl}/orders/\${orderNumber}\`,
      {
        headers: {
          'X-API-Key': API_KEY,
          'X-API-Secret': API_SECRET
        }
      }
    );

    console.log('Status:', response.data.data.status);
    return response.data;
  } catch (error) {
    console.error('Error:', error.response?.data);
  }
}`,
  },
  php: {
    name: 'PHP',
    icon: faPhp,
    createOrder: `<?php

$apiKey = '${apiKey}';
$apiSecret = 'sk_your_api_secret';

$data = [
    'amount' => 100.00,
    'customer_name' => 'นาย A',
    'customer_email' => 'customer@example.com',
    'customer_phone' => '0812345678',
    'reference_id' => 'YOUR-ORDER-123',
    'bank_name' => 'SCB',
    'bank_number' => '1234567890'
];

$ch = curl_init('${baseUrl}/orders');
curl_setopt($ch, CURLOPT_POST, true);
curl_setopt($ch, CURLOPT_HTTPHEADER, [
    "X-API-Key: $apiKey",
    "X-API-Secret: $apiSecret",
    'Content-Type: application/json'
]);
curl_setopt($ch, CURLOPT_POSTFIELDS, json_encode($data));
curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);

$response = curl_exec($ch);
$result = json_decode($response, true);

echo "Order Number: " . $result['data']['order_number'];
echo "\\nPayment URL: " . $result['data']['payment_url'];

curl_close($ch);
?>`,
    checkStatus: `<?php

$orderNumber = 'ORD1728374929123';

$ch = curl_init("${baseUrl}/orders/$orderNumber");
curl_setopt($ch, CURLOPT_HTTPHEADER, [
    "X-API-Key: $apiKey",
    "X-API-Secret: $apiSecret"
]);
curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);

$response = curl_exec($ch);
$result = json_decode($response, true);

echo "Status: " . $result['data']['status'];

curl_close($ch);
?>`,
  },
  python: {
    name: 'Python',
    icon: faPython,
    createOrder: `import requests

API_KEY = '${apiKey}'
API_SECRET = 'sk_your_api_secret'

headers = {
    'X-API-Key': API_KEY,
    'X-API-Secret': API_SECRET,
    'Content-Type': 'application/json'
}

data = {
    'amount': 100.00,
    'customer_name': 'นาย A',
    'customer_email': 'customer@example.com',
    'reference_id': 'YOUR-ORDER-123',
    'bank_name': 'SCB',
    'bank_number': '1234567890'
}

response = requests.post(
    '${baseUrl}/orders',
    headers=headers,
    json=data
)

result = response.json()
print(f"Order: {result['data']['order_number']}")
print(f"URL: {result['data']['payment_url']}")`,
    checkStatus: `order_number = 'ORD1728374929123'

response = requests.get(
    f'${baseUrl}/orders/{order_number}',
    headers=headers
)

result = response.json()
print(f"Status: {result['data']['status']}")`,
  },
});

/** ตัวอย่างการสร้าง HMAC signature (V2) — 3 ภาษา เรียงตามที่แสดงในหน้า Authentication */
export const buildSignatureSamples = (apiKey: string): Array<{ label: string; id: string; code: string }> => [
  {
    label: 'javascript',
    id: 'sig-js',
    code: `const crypto = require('crypto');

const API_KEY = '${apiKey}';
const SECRET_KEY = 'sk_your_api_secret';

function createSignature(method, path, body = {}) {
  const timestamp = Math.floor(Date.now() / 1000).toString();
  let payload = timestamp + method.toUpperCase() + path;
  if (Object.keys(body).length > 0) {
    payload += JSON.stringify(body);
  }
  const signature = crypto
    .createHmac('sha256', SECRET_KEY)
    .update(payload)
    .digest('hex');
  return { timestamp, signature };
}

// Usage
const body = { amount: 100, bank_name: 'SCB', bank_number: '1234567890', reference_id: 'ORDER-123' };
const { timestamp, signature } = createSignature('POST', '/api/v2/orders', body);

const headers = {
  'X-API-Key': API_KEY,
  'X-Timestamp': timestamp,
  'X-Signature': signature,
  'Content-Type': 'application/json'
};`,
  },
  {
    label: 'php',
    id: 'sig-php',
    code: `<?php
$apiKey = '${apiKey}';
$secretKey = 'sk_your_api_secret';

function createSignature($method, $path, $body = [], $secretKey) {
    $timestamp = time();
    $payload = $timestamp . strtoupper($method) . $path;
    if (!empty($body)) {
        $payload .= json_encode($body);
    }
    $signature = hash_hmac('sha256', $payload, $secretKey);
    return ['timestamp' => $timestamp, 'signature' => $signature];
}

$body = ['amount' => 100, 'bank_name' => 'SCB', 'bank_number' => '1234567890', 'reference_id' => 'ORDER-123'];
$sig = createSignature('POST', '/api/v2/orders', $body, $secretKey);

$headers = [
    "X-API-Key: $apiKey",
    "X-Timestamp: {$sig['timestamp']}",
    "X-Signature: {$sig['signature']}",
    "Content-Type: application/json"
];
?>`,
  },
  {
    label: 'python',
    id: 'sig-py',
    code: `import hmac
import hashlib
import json
import time

API_KEY = '${apiKey}'
SECRET_KEY = 'sk_your_api_secret'

def create_signature(method, path, body=None):
    timestamp = str(int(time.time()))
    payload = timestamp + method.upper() + path
    if body:
        payload += json.dumps(body, separators=(',', ':'))
    signature = hmac.new(
        SECRET_KEY.encode(), payload.encode(), hashlib.sha256
    ).hexdigest()
    return timestamp, signature

body = {'amount': 100, 'bank_name': 'SCB', 'bank_number': '1234567890', 'reference_id': 'ORDER-123'}
timestamp, signature = create_signature('POST', '/api/v2/orders', body)

headers = {
    'X-API-Key': API_KEY,
    'X-Timestamp': timestamp,
    'X-Signature': signature,
    'Content-Type': 'application/json'
}`,
  },
];
